import { z } from 'zod'

/**
 * Generic ingestion payload for POST-based data sources.
 *
 * One payload = one ingestion event = one `ingestion_` row + N `sensor_reading_` rows.
 *
 * Wire format uses snake_case to align with `sensor_reading_` column names and
 * external API conventions. Any vendor that posts data via API key auth uses
 * this contract; the manufacturer prefix (`{manufacturer}:{device_id}`) on
 * `logger_sn` is applied server-side based on the authenticated API key, so
 * `device_id` here is the raw vendor identifier.
 */

const ingestionReadingSchema = z.object({
  /** Sensor identifier. Maps to `sensor_reading_.sensor_sn`. */
  sensor_id: z.string().min(1),

  /** ISO 8601 timestamp with timezone offset for when the measurement was taken. */
  timestamp: z.iso.datetime({ offset: true }),

  /**
   * Measurement type. Must match an `id` in the `sensor_measurement_kind_`
   * lookup table. Maps to `sensor_reading_.sensor_measurement_kind`.
   */
  measurement_type: z.string().min(1),

  /** The measured value. Must be a finite number (no NaN/Infinity). */
  value: z.number().refine(Number.isFinite, 'value must be finite'),

  /**
   * Unit. Optional -- when omitted, falls back to
   * `sensor_measurement_kind_.default_unit`. Maps to `sensor_reading_.unit`.
   */
  unit: z.string().min(1).optional(),

  /**
   * Aggregation method. Optional -- defaults to `'CURRENT'`. Must match an `id`
   * in the `data_aggregation_method_` lookup table. Maps to
   * `sensor_reading_.data_aggregation_method`.
   */
  aggregation_type: z.string().min(1).optional(),
})

export type IngestionReading = z.infer<typeof ingestionReadingSchema>

export const ingestionPayloadSchema = z.object({
  /**
   * Manufacturer name. Optional -- device-key auth resolves this from the
   * logger row. CAPTUREAPP defaults to 'beadedstream'. When provided, must
   * match the resolved manufacturer (case-insensitive).
   */
  manufacturer: z.string().min(1).optional(),

  /**
   * Logger/device identifier (raw, vendor-side). Server prefixes with the
   * authenticated manufacturer to produce `sensor_reading_.logger_sn`.
   */
  device_id: z.string().min(1),

  /**
   * Optional client-supplied idempotency key. Stored on
   * `ingestion_.idempotency_key` and enforced unique per source by the
   * partial index `idx_ingestion_idempotency`. Retried POSTs with the same
   * key collide at the DB level rather than creating duplicate `ingestion_`
   * rows or redundant S3 uploads.
   */
  idempotency_key: z.string().min(1).optional(),

  /**
   * Optional free-form metadata, merged into `ingestion_.metadata` alongside
   * any server-derived fields. Caller-defined shape.
   */
  metadata: z.record(z.string(), z.unknown()).optional(),

  /** One or more sensor readings. Capped at 1000 per payload. */
  readings: z.array(ingestionReadingSchema).min(1).max(1000),
})

export type IngestionPayload = z.infer<typeof ingestionPayloadSchema>

/**
 * Server response after processing an ingestion payload.
 *
 * `readings_received` and `readings_inserted` differ when dedup drops
 * already-present rows -- useful for clients replaying buffered data.
 */
export const ingestionResponseSchema = z.object({
  ingestion_id: z.uuid(),
  readings_received: z.number().int().nonnegative(),
  readings_inserted: z.number().int().nonnegative(),
})

export type IngestionResponse = z.infer<typeof ingestionResponseSchema>
